import React, { useEffect, useState, useRef, useMemo } from "react";
import FormTemplate from "../../../forms/FormTemplate";
import {
  Box,
  Button,
  Stack,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  lighten,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "../../../styles/SMSSideMenu.css";
import useAutomations from "../../../hooks/automationhooks/useAutomations";
import SMSTemplateSelection from "./SMSTemplateSelection";
import { useParams } from "react-router-dom";
import SelectArrow from "../../../styles/icons/SelectArrow";
import ROUTES from "../../../routerfolder/Routes";
import SMSCreateSenderMenu from "./SMSCreateSenderMenu";
import TriggerModeHeaderControl from "./TriggerModeHeaderControl";

export default function SMSSideMenu({
  onClose,
  onSubmit,
  selectedNode,
  preSelectedTemplate = null,
}) {
  const COLOR_SURFACE = "#2C2B2D";
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const CHANGE_TEMPLATE_ICON_BG =
    "url(\"data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17 7H1L6.5 1M1 11H17L11.5 17' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";

  const SELECT_MENU_PROPS = {
    PaperProps: {
      sx: {
        mt: 0.5,
        maxHeight: 320,
        overflowY: "auto",
        bgcolor: COLOR_HEADER,
        backgroundImage: "none",
        color: "#FFFFFF",
        border: `1px solid ${COLOR_ACCENT}`,
        borderRadius: "3px",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-track": { background: COLOR_HEADER },
        "&::-webkit-scrollbar-thumb": {
          background: "#FFFFFF",
          borderRadius: "3px",
        },
        "& .MuiMenuItem-root": {
          minHeight: 42,
          color: "#FFFFFF",
          fontSize: "14px",
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "rgba(13,163,226,0.22)",
          },
          "&:hover": {
            backgroundColor: "rgba(13,163,226,0.12)",
          },
        },
      },
    },
  };

  const { brandId } = useParams();
  const {
    isLoading,
    smsTemplates,
    smsSenders,
    handleGetSmsSenders,
    handleGetSmsTemplates,
    handleCreateSmsSender,
  } = useAutomations();

  /* ---------------- local state ---------------- */
  const [tplSelOpen, setTplSelOpen] = useState(false);
  const [senderOpen, setSenderOpen] = useState(false);

  const [selectedTpl, setSelectedTpl] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [templateParams, setTemplateParams] = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [selectedSender, setSelectedSender] = useState("");
  const [triggerMode, setTriggerMode] = useState("regular");

  // send timing
  const [sendTiming, setSendTiming] = useState("immediate"); // 'immediate' | 'scheduled'
  const [timeToSend, setTimeToSend] = useState(null); // string with offset: YYYY-MM-DDTHH:mm:ssZ

  /* guard so we only pre‑handle once */
  const preHandled = useRef(false);

  /* ------------ initial data fetch ------------- */
  useEffect(() => {
    handleGetSmsSenders(brandId);
    handleGetSmsTemplates(brandId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------- hydrate when editing existing node ------------- */
  useEffect(() => {
    if (!selectedNode) return;
    if (!smsTemplates?.data?.length) return;

    const { template, sendFrom } = selectedNode.data ?? {};

    if (template?.value) {
      if (!selectedTpl || selectedTpl.Id !== template.value) {
        const match = smsTemplates.data.find((t) => t.Id === template.value);
        if (match) {
          applyTemplate(match, template.placeholderMappings || {});
        }
      }
    }

    if (sendFrom?.value && selectedSender === "") {
      setSelectedSender(sendFrom.value);
    }

    const existingTriggerMode = selectedNode.data?.triggerMode;
    if (existingTriggerMode === "api" || existingTriggerMode === "regular") {
      setTriggerMode(existingTriggerMode);
    }

    // hydrate scheduling
    const existingTiming = selectedNode.data?.sendTiming?.value;
    const existingTimeToSend = selectedNode.data?.timeToSend;
    if (
      existingTiming &&
      (existingTiming === "immediate" || existingTiming === "scheduled")
    ) {
      setSendTiming(existingTiming);
    } else if (existingTimeToSend) {
      // backward/partial compatibility: if timeToSend exists, treat as scheduled
      setSendTiming("scheduled");
    }
    if (existingTimeToSend && !timeToSend) {
      setTimeToSend(existingTimeToSend);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, smsTemplates?.data]);

  /* ---------------- creation pre‑selection ---------------- */
  useEffect(() => {
    if (preSelectedTemplate && !selectedNode && !preHandled.current) {
      applyTemplate(preSelectedTemplate);
      preHandled.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedTemplate, selectedNode]);

  /* ------------- derived validity ------------- */
  const isValid = useMemo(() => {
    const filled = templateParams.every(
      (k) => placeholderValues[k] && placeholderValues[k] !== "null",
    );
    const hasSchedule = sendTiming !== "scheduled" || Boolean(timeToSend);
    return (
      Boolean(selectedTpl) && filled && Boolean(selectedSender) && hasSchedule
    );
  }, [
    selectedTpl,
    placeholderValues,
    templateParams,
    selectedSender,
    sendTiming,
    timeToSend,
  ]);

  /* ================= helpers ================= */
  const applyTemplate = (tpl, savedMappings = {}) => {
    const params =
      tpl.TemplateParameters ??
      Array.from(new Set((tpl.Content || "").match(/(\[#.*?#\])/g) || []));

    const initMap = {};
    params.forEach((p) => {
      initMap[p] = savedMappings[p] ?? "null";
    });

    setSelectedTpl(tpl);
    setMessageText(tpl.Content);
    setTemplateParams(params);
    setPlaceholderValues(initMap);
  };

  const handleTemplateChosen = (tpl) => {
    if (!selectedTpl || selectedTpl.Id !== tpl.Id) {
      applyTemplate(tpl);
    }
    setTplSelOpen(false);
  };

  const handlePlaceholderChange = (ph, val) => {
    setPlaceholderValues((prev) => ({ ...prev, [ph]: val }));
  };

  const handleNewSender = async (brand, senderName) => {
    await handleCreateSmsSender(brand, senderName);
    console.log("await ended");
    console.log(brand);
    console.log(senderName);

    handleGetSmsSenders(brand);
  };

  const handleSave = () => {
    if (!isValid) return;

    // Data payload used when creating a NEW node
    const dataPayload = {
      label: "SMS Message",
      triggerMode,
      sendFrom: { value: selectedSender, label: selectedSender },
      sendTiming: {
        value: sendTiming,
        label: sendTiming === "scheduled" ? "Scheduled" : "Immediate",
      },
      timeToSend: sendTiming === "scheduled" ? timeToSend : null,
      template: {
        value: selectedTpl.Id,
        label: selectedTpl.TemplateName,
        placeholderMappings: { ...placeholderValues },
      },
    };

    if (selectedNode) {
      // --- EDIT FLOW --------------------------------------------------
      // Mutate the existing node's data (same pattern used originally)
      selectedNode.data.sendFrom = {
        value: selectedSender,
        label: selectedSender,
      };
      selectedNode.data.triggerMode = triggerMode;
      selectedNode.data.sendTiming = {
        value: sendTiming,
        label: sendTiming === "scheduled" ? "Scheduled" : "Immediate",
      };
      selectedNode.data.timeToSend =
        sendTiming === "scheduled" ? timeToSend : null;
      selectedNode.data.template.value = selectedTpl.Id;
      selectedNode.data.template.label = selectedTpl.TemplateName;
      selectedNode.data.template.placeholderMappings = { ...placeholderValues };

      // let NodeSideMenu know which template was chosen so it can update
      onSubmit(selectedTpl);
    } else {
      // --- CREATION FLOW ---------------------------------------------
      // Pass full data for addNodeBetweenNodes
      onSubmit(dataPayload);
    }

    onClose();
  };

  /* -------- render placeholder‑rich text -------- */
  const renderRichMessage = (txt) =>
    txt.split(/(\[#.*?#\])/g).map((part, i) =>
      /(\[#.*?#\])/.test(part) ? (
        <Select
          key={i}
          size="medium"
          value={placeholderValues[part] || "null"}
          onChange={(e) => handlePlaceholderChange(part, e.target.value)}
          IconComponent={SelectArrow}
          MenuProps={SELECT_MENU_PROPS}
          sx={{
            width: "216px",
            minWidth: "216px",
            height: "56px",
            mx: "10px",
            my: "4px",
            color: "#FFFFFF",
            bgcolor: COLOR_HEADER,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: COLOR_ACCENT,
              borderRadius: "0px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: COLOR_ACCENT,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: COLOR_ACCENT,
            },
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              padding: "0 14px",
            },
          }}
        >
          <MenuItem value="null" disabled>
            Select
          </MenuItem>
          {["FirstName", "LastName", "FullName", "Id", "Phone", "QrCode"].map(
            (opt) => (
              <MenuItem key={opt} value={opt}>
                {opt === "QrCode" ? "QR Code" : opt}
              </MenuItem>
            ),
          )}
        </Select>
      ) : (
        <span key={i}>{part}</span>
      ),
    );

  if (isLoading)
    return <Typography sx={{ p: 2 }}>Loading templates…</Typography>;

  /* ================= JSX ================= */
  return (
    <>
      <FormTemplate
        title={selectedNode ? "Edit SMS" : "Create SMS"}
        isValid={isValid}
        handleSubmit={handleSave}
        onClose={onClose}
        headerRightAction={
          <TriggerModeHeaderControl value={triggerMode} onChange={setTriggerMode} />
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* top buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="companyBlue"
              sx={{
                width: "229px",
                minHeight: "50px",
                borderRadius: "7px",
                textTransform: "none",
                boxShadow: 0,
                color: "#FFFFFF",
                px: "12px",
                py: "10px",
                gap: "10px",
                "&:hover": {
                  backgroundColor: (theme) =>
                    lighten(theme.palette.companyBlue.main, 0.1),
                },
              }}
              onClick={() => setTplSelOpen(true)}
            >
              <Box
                component="span"
                sx={{
                  width: 18,
                  height: 18,
                  display: "inline-block",
                  backgroundImage: CHANGE_TEMPLATE_ICON_BG,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  backgroundSize: "contain",
                }}
              />
              <Typography sx={{ fontFamily: "Assistant, sans-serif", fontWeight: 700, fontSize: "22px", lineHeight: 1, color: "#FFFFFF", whiteSpace: "nowrap" }}>
                Select Template
              </Typography>
            </Button>
            <Button
              variant="outlined"
              sx={{
                width: "187px",
                minHeight: "50px",
                borderRadius: "7px",
                textTransform: "none",
                color: "#FFFFFF",
                boxShadow: 0,
                borderColor: COLOR_ACCENT,
                "&:hover": {
                  borderColor: COLOR_ACCENT,
                  bgcolor: "rgba(13, 163, 226, 0.08)",
                },
              }}
              onClick={() => setSenderOpen(true)}
            >
              <Typography sx={{ fontFamily: "Assistant, sans-serif", fontWeight: 700, fontSize: "18px", lineHeight: 1, color: "#FFFFFF" }}>
                Create Sender
              </Typography>
            </Button>
          </Stack>

          {/* template preview */}
          {selectedTpl ? (
            <Box>
              <Typography>Selected Template:</Typography>
              <Box
                sx={{
                  border: "1px solid rgba(255,255,255,0.55)",
                  borderRadius: "4px",
                  p: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  bgcolor: COLOR_SURFACE,
                }}
              >
                <Typography variant="body2">
                  {renderRichMessage(messageText)}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography color="#EB4D4D">No Template Selected</Typography>
          )}

          {/* sender picker */}
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "flex-start", flexWrap: "wrap" }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <InputLabel sx={{ mt: 2, color: COLOR_ACCENT, "&.Mui-focused": { color: COLOR_ACCENT } }}>Select Sender</InputLabel>
              <Select
                value={selectedSender}
                onChange={(e) => setSelectedSender(e.target.value)}
                IconComponent={SelectArrow}
                MenuProps={SELECT_MENU_PROPS}
                sx={{
                  minWidth: 240,
                  height: 56,
                  color: "#FFFFFF",
                  bgcolor: COLOR_HEADER,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLOR_ACCENT,
                    borderRadius: "7px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLOR_ACCENT,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLOR_ACCENT,
                  },
                }}
              >
                {smsSenders.data && smsSenders.data.length ? (
                  smsSenders.data.map((s) => (
                    <MenuItem key={s.SenderName} value={s.SenderName}>
                      {s.SenderName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No senders found</MenuItem>
                )}
              </Select>
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", minWidth: 520 }}
            >
              <Typography
                sx={{ mt: 2, mb: 0.5, fontSize: 14, fontWeight: 500 }}
              >
                When should it be sent
              </Typography>

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center",
                  flexWrap: "wrap",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                }}
              >
                {/* Left: Immediate option */}
                <FormControlLabel
                  value="immediate"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: (theme) => theme.palette.companyBlue.main,
                        "&.Mui-checked": {
                          color: (theme) => theme.palette.companyBlue.main,
                        },
                      }}
                      checked={sendTiming === "immediate"}
                      onChange={() => {
                        setSendTiming("immediate");
                        setTimeToSend(null);
                      }}
                    />
                  }
                  label="Immediate"
                  sx={{ m: 0 }}
                />

                {/* Right: Scheduled radio + date input */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ ml: "auto", alignItems: "center" }}
                >
                  <Radio
                    size="small"
                    sx={{
                      color: (theme) => theme.palette.companyBlue.main,
                      "&.Mui-checked": {
                        color: (theme) => theme.palette.companyBlue.main,
                      },
                    }}
                    checked={sendTiming === "scheduled"}
                    onChange={() => setSendTiming("scheduled")}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={timeToSend ? dayjs(timeToSend) : null}
                      onChange={(newVal) => {
                        setTimeToSend(
                          newVal ? newVal.format("YYYY-MM-DDTHH:mm:ssZ") : null,
                        );
                      }}
                      disabled={sendTiming !== "scheduled"}
                      disablePast
                      ampm={false}
                      format="DD/MM/YYYY HH:mm"
                      slotProps={{
                        textField: {
                          size: "small",
                          placeholder: "Set date and time",
                          error: sendTiming === "scheduled" && !timeToSend,
                          sx: {
                            minWidth: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Stack>

              {/* Reserve space so the sender field doesn't "jump" when error appears */}
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.75, minHeight: 18 }}
              >
                {sendTiming === "scheduled" && !timeToSend
                  ? "Please pick a date & time"
                  : "\u00A0"}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </FormTemplate>

      {/* template selection dialog */}
      <SMSTemplateSelection
        open={tplSelOpen}
        setSelected={handleTemplateChosen}
        setClosed={() => setTplSelOpen(false)}
        templates={smsTemplates?.data || []}
        refreshTemplates={() => handleGetSmsTemplates(brandId)}
      />

      {/* create sender dialog */}
      <SMSCreateSenderMenu
        open={senderOpen}
        setOpen={setSenderOpen}
        createNewSender={handleNewSender}
        brand={brandId}
      />
    </>
  );
}
